import { useState } from 'react';
import { Box, BoxProps, Button, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { ExtAttachmentType } from 'api/extForms';
import { useIsMdBreakpoint } from 'hooks';
import { FileItem } from './FileItem';
import { FilesModal } from './FilesModal';
import { Page } from './Page';

export type FilesStepProps = BoxProps & {
  filesDescription: ExtAttachmentType[];
  prevStep: () => void;
  nextStep: () => void;
};

export const AttachmentsStep: React.FC<FilesStepProps> = ({
  filesDescription = [],
  prevStep,
  nextStep,
  ...boxProps
}) => {
  const isMdBreakpoint = useIsMdBreakpoint();
  const nestedPath = 'fileAttachments';
  const { trigger, getValues } = useFormContext();
  const { t } = useTranslation();
  const [showOptionalFilesModal, setShowOptionalFilesModal] = useState(false);
  const emptyFiles = filesDescription.filter(
    ({ uuid }) => !getValues(`${nestedPath}.${uuid}`),
  );

  const goToNextStep = async () => {
    const isValid = await trigger(nestedPath, { shouldFocus: true });

    if (isValid) {
      if (emptyFiles.length) {
        setShowOptionalFilesModal(true);
      } else {
        nextStep();
      }
    }
  };

  const handleApprove = () => {
    setShowOptionalFilesModal(false);
    nextStep();
  };

  const handleBack = () => {
    setShowOptionalFilesModal(false);
  };

  return (
    <>
      <FilesModal
        size="lg"
        opened={showOptionalFilesModal}
        centered
        onClose={handleBack}
        offerFiles={emptyFiles}
        onBack={handleBack}
        onApprove={handleApprove}
      />
      <Page>
        <Stack
          spacing="lg"
          sx={{
            overflow: 'auto',
          }}
        >
          {filesDescription.map(fileSetup => (
            <FileItem
              key={fileSetup.uuid}
              name={`${nestedPath}.${fileSetup.uuid}`}
              fileDescription={fileSetup}
            />
          ))}
        </Stack>
      </Page>
      <Page py={16}>
        <Box
          sx={theme => ({
            display: 'flex',
            columnGap: theme.spacing.sm,
            justifyContent: isMdBreakpoint ? 'flex-end' : 'center',
          })}
          {...boxProps}
        >
          <Button variant="default" onClick={() => prevStep()}>
            {t('common:back')}
          </Button>
          <Button onClick={goToNextStep}>{t('common:next')}</Button>
        </Box>
      </Page>
    </>
  );
};
